import { useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { User, CreateUserData, UpdateUserData, Role } from '../../services/user.service';

const createUserSchema = (t: (key: string) => string) => z.object({
  username: z.string().min(1, t('users.username_required')).max(50),
  email: z.string().email(t('users.email_invalid')).max(255),
  password: z.string().min(8, t('users.password_min_length')).optional().or(z.literal('')),
  firstName: z.string().min(1, t('users.first_name_required')).max(100),
  lastName: z.string().min(1, t('users.last_name_required')).max(100),
  roleId: z.number().int().positive(t('users.role_id_required')),
  organizationName: z.string().max(255).nullable().optional(),
  departmentName: z.string().max(255).nullable().optional(),
  timezone: z.string().max(50).optional(),
  language: z.string().max(10).optional(),
});

const updateUserSchema = (t: (key: string) => string) => createUserSchema(t).extend({
  password: z.string().min(8, t('users.password_min_length')).optional().or(z.literal('')),
});

type UserFormData = z.infer<ReturnType<typeof createUserSchema>>;

interface UserFormModalProps {
  show: boolean;
  onHide: () => void;
  user: User | null;
  roles: Role[];
  onSubmit: (data: CreateUserData | UpdateUserData) => void;
  isLoading: boolean;
}

export default function UserFormModal({
  show,
  onHide,
  user,
  roles,
  onSubmit,
  isLoading,
}: UserFormModalProps) {
  const { t } = useTranslation();
  const isEdit = !!user;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(isEdit ? updateUserSchema(t) : createUserSchema(t)),
    defaultValues: {
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      roleId: roles[0]?.id || 2,
      organizationName: null,
      departmentName: null,
      timezone: 'Asia/Tokyo',
      language: 'ja',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        username: user.username,
        email: user.email,
        password: '',
        firstName: user.firstName,
        lastName: user.lastName,
        roleId: user.roleId,
        organizationName: user.organizationName || null,
        departmentName: user.departmentName || null,
        timezone: user.timezone || 'Asia/Tokyo',
        language: user.language || 'ja',
      });
    } else {
      reset({
        username: '',
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        roleId: roles[0]?.id || 2,
        organizationName: null,
        departmentName: null,
        timezone: 'Asia/Tokyo',
        language: 'ja',
      });
    }
  }, [user, reset, roles]);

  const onSubmitForm = (data: UserFormData) => {
    const submitData: any = { ...data };
    // パスワードが空の場合は送信しない（更新時）
    if (isEdit && !submitData.password) {
      delete submitData.password;
    }
    onSubmit(submitData);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? t('users.modal_edit_title') : t('users.modal_create_title')}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-username">
              {t('users.field_username_label')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              id="user-username"
              name="username"
              type="text"
              {...register('username')}
              isInvalid={!!errors.username}
            />
            <Form.Control.Feedback type="invalid">
              {errors.username?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-email">
              {t('users.field_email_label')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              id="user-email"
              name="email"
              type="email"
              {...register('email')}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-password">
              {t('users.field_password_label')} {!isEdit && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              id="user-password"
              name="password"
              type="password"
              {...register('password')}
              isInvalid={!!errors.password}
              placeholder={isEdit ? t('users.password_change_placeholder') : ''}
            />
            <Form.Control.Feedback type="invalid">
              {errors.password?.message}
            </Form.Control.Feedback>
            {isEdit && (
              <Form.Text className="text-muted">
                {t('users.password_no_change_note')}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-last-name">
              {t('users.field_last_name_label')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              id="user-last-name"
              name="lastName"
              type="text"
              {...register('lastName')}
              isInvalid={!!errors.lastName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lastName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-first-name">
              {t('users.field_first_name_label')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              id="user-first-name"
              name="firstName"
              type="text"
              {...register('firstName')}
              isInvalid={!!errors.firstName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.firstName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-role">
              {t('users.field_role_label_form')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              id="user-role"
              name="roleId"
              {...register('roleId', { valueAsNumber: true })}
              isInvalid={!!errors.roleId}
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name} - {role.description}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.roleId?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-organization">{t('users.field_organization_label')}</Form.Label>
            <Form.Control
              id="user-organization"
              name="organizationName"
              type="text"
              {...register('organizationName')}
              isInvalid={!!errors.organizationName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.organizationName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-department">{t('users.field_department_label')}</Form.Label>
            <Form.Control
              id="user-department"
              name="departmentName"
              type="text"
              {...register('departmentName')}
              isInvalid={!!errors.departmentName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.departmentName?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-timezone">{t('users.field_timezone_label')}</Form.Label>
            <Form.Select
              id="user-timezone"
              name="timezone"
              {...register('timezone')}
              isInvalid={!!errors.timezone}
            >
              <option value="Asia/Tokyo">{t('users.timezone_asia_tokyo')}</option>
              <option value="UTC">{t('users.timezone_utc')}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="user-language">{t('users.field_language_label')}</Form.Label>
            <Form.Select
              id="user-language"
              name="language"
              {...register('language')}
              isInvalid={!!errors.language}
            >
              <option value="ja">{t('common.language_ja')}</option>
              <option value="en">{t('common.language_en')}</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? t('common.processing') : isEdit ? t('common.update') : t('auth.register')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

